import { WSCard, WSElement, WSText } from "@wingspanhq/fe-component-library";
import { formatMoney } from "@wingspanhq/utils/dist/formatMoney";
import { ClientWorkFlowStatus } from "@wingspanhq/payments/dist/interfaces";
import React from "react";
import { useBrowserPageTitle } from "../../../components/BrowserPageTitle/BrowserPageTitle";
import { useQueryPayables } from "../../../query/payments/queries";
import { WSQueries } from "../../../query/WSQuery";
import {
  PayablesFilters,
  PayablesStatusFilter,
  PayablesTable
} from "../../components/NewPayablesTable/PayablesTable";
import { useDefaultCurrency } from "../../../shared/hooks/useDefaultCurrency";
import { API_QUERY_PAYABLES_DUAL_STAGE_TO_APPROVE } from "../../utils/payables/apiQueries";
import { useDualStageApproveAll } from "../../utils/payables/approveAll";
import { useDualStageApproveAllBulkAction } from "../../utils/payables/approveAllBulkAction";
import { useFeatureFlags } from "../../../query/hooks/useFeatureFlags";

const DEFAULT_FILTERS: PayablesFilters = {
  status: [PayablesStatusFilter.ToApprove],
  custom: {
    "client.workflowStatus": ClientWorkFlowStatus.PreApproved
  }
};

export const PayablesDualStageToApprove: React.FC = () => {
  useBrowserPageTitle("Payables To Approve");
  const defaultCurrency = useDefaultCurrency();
  const featureFlags = useFeatureFlags();

  const queryPayablesToApprove = useQueryPayables({
    ...API_QUERY_PAYABLES_DUAL_STAGE_TO_APPROVE,
    page: {
      size: 1
    }
  });

  const legacyApproveAll = useDualStageApproveAll();
  const approveAllBulkAction = useDualStageApproveAllBulkAction();

  const isBulkActions = featureFlags.data?.bulkPayableActions;

  return (
    <>
      <WSText.Heading1>To Approve</WSText.Heading1>
      <WSText color="gray600" mb="2XL">
        All payables and requests that are not yet scheduled to be paid.
      </WSText>

      <WSElement mb="2XL">
        <WSQueries
          queries={{ queryPayablesToApprove }}
          renderErrors={() => null}
        >
          {({
            queryPayablesToApprove: {
              data: {
                data: payablesToApprove,
                summary: toApprovePayablesSummary
              }
            }
          }) => (
            <>
              {toApprovePayablesSummary.listSize > 1 && (
                <WSCard
                  mb="XL"
                  header={{
                    label: {
                      avatar: {
                        type: "icon",
                        icon: "dollar-circle",
                        size: "M",
                        color: "amber400",
                        colorBackground: "amber50"
                      },
                      text: "Pending approval:"
                    }
                  }}
                  divider="bottom"
                  actions={{
                    alignment: "left",
                    buttons: [
                      {
                        label: `Approve ${toApprovePayablesSummary.listSize} payments`,
                        kind: "Link",
                        rightIcon: "chevron-right",
                        onClick: isBulkActions
                          ? () =>
                              approveAllBulkAction(
                                toApprovePayablesSummary.listSize
                              )
                          : legacyApproveAll.act,
                        loading: isBulkActions
                          ? undefined
                          : legacyApproveAll.isLoading
                      }
                    ]
                  }}
                >
                  <WSText.ParagraphSm color="gray500">
                    {toApprovePayablesSummary.listSize} payables totaling{" "}
                    {formatMoney(
                      toApprovePayablesSummary.totalValue,
                      defaultCurrency
                    )}
                  </WSText.ParagraphSm>
                </WSCard>
              )}
            </>
          )}
        </WSQueries>
      </WSElement>

      <PayablesTable
        bulkActions={
          isBulkActions
            ? ["approve", "unapprove", "cancel"]
            : ["approve", "unapprove", "delete"]
        }
        visibleFilters={{
          payee: true,
          collaboratorReviewStatus: true,
          openedAtRange: true,
          dueDateRange: true
        }}
        basePath="/member/invoices/payables/to-approve"
        defaultFilters={DEFAULT_FILTERS}
        showHeaders
        showDateRequested
        showDueDate
        emptyState={{
          firstTime:
            "Every requested, scheduled, and paid payable will display here.",
          standart:
            "You’re all caught up! Nothing is waiting for your approval."
        }}
      />
    </>
  );
};
